import * as THREE from 'three';

export type DinoKind =
  | 'compy'
  | 'diloph'
  | 'baryonyx'
  | 'raptor'
  | 'troodon'
  | 'rex'
  | 'para'
  | 'deinonychus'
  | 'galli'
  | 'trike'
  | 'stego'
  | 'anky'
  | 'carno'
  | 'spino'
  | 'ptera';

export type Aabb2 = {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
};

export type DinoKit = {
  kind: DinoKind;
  taxon: string;
  hip: number;
  hp: number;
  wanderSpeed: number;
  fleeSpeed: number;
  combatSpeed: number;
  hearRange: number;
  fleeRange: number;
  spitRange: number;
  peckRange: number;
  /** Compy only deals damage when player HP is below this. */
  lethalHp: number;
  /** Optional authored GLB. DinoActor swaps it over the primitive mesh. */
  glb?: string;
  build(): THREE.Group;
};

const COMPY_HIDE = 0x6a5a3c;
const COMPY_BELLY = 0x9a8a62;
const DILOPH_HIDE = 0x4a5340;
const DILOPH_BELLY = 0x8a7a58;
const DILOPH_CREST = 0x7a5a40;
const DILOPH_FRILL = 0x5a4030;
const RAPTOR_HIDE = 0x5c4a30;
const RAPTOR_BELLY = 0x8e7a52;
const RAPTOR_STRIPE = 0x3a2e1c;
const CLAW = 0x2a2418;

export const COMPY_KIT: DinoKit = {
  kind: 'compy',
  taxon: 'Compsognathus longipes',
  hip: 0.4,
  hp: 12,
  wanderSpeed: 1.6,
  fleeSpeed: 5.5,
  combatSpeed: 4.2,
  hearRange: 8,
  fleeRange: 4,
  spitRange: 0,
  peckRange: 1.1,
  lethalHp: 15,
  glb: '/models/dino/dino_compy.glb',
  build: buildCompyMesh,
};

export const DILOPH_KIT: DinoKit = {
  kind: 'diloph',
  taxon: 'Dilophosaurus wetherilli',
  hip: 1.4,
  hp: 80,
  wanderSpeed: 1.7,
  fleeSpeed: 4.4,
  combatSpeed: 3.6,
  hearRange: 16,
  fleeRange: 0,
  spitRange: 12,
  peckRange: 1.6,
  lethalHp: 0,
  glb: '/models/dino/dino_diloph.glb',
  build: buildDilophMesh,
};

const BARY_HIDE = 0x4a5348;
const BARY_BELLY = 0x8a7e5c;
const BARY_CLAW = 0x2a2418;

/** Long-snouted river hunter, hip ~0.55 so the body sits in the Glassvein sheet. */
export const BARYONYX_KIT: DinoKit = {
  kind: 'baryonyx',
  taxon: 'Baryonyx walkeri',
  hip: 0.55,
  hp: 140,
  wanderSpeed: 1.4,
  fleeSpeed: 3.2,
  combatSpeed: 2.6,
  hearRange: 18,
  fleeRange: 0,
  spitRange: 5,
  peckRange: 2.4,
  lethalHp: 0,
  glb: '/models/dino/dino_baryonyx.glb',
  build: buildBaryonyxMesh,
};

export const RAPTOR_KIT: DinoKit = {
  kind: 'raptor',
  taxon: 'Velociraptor mongoliensis',
  hip: 1.6,
  hp: 45,
  wanderSpeed: 2.4,
  fleeSpeed: 9.2,
  combatSpeed: 11,
  hearRange: 18,
  fleeRange: 0,
  spitRange: 0,
  peckRange: 1.5,
  lethalHp: 0,
  glb: '/models/dino/dino_raptor.glb',
  build: buildRaptorMesh,
};

const TROODON_HIDE = 0x2e342c;
const TROODON_BELLY = 0x8a7e5c;
const TROODON_EYE = 0x141816;

/** Night Pulse hunter. Authored GLB at /models/dino/dino_troodon.glb. */
export const TROODON_KIT: DinoKit = {
  kind: 'troodon',
  taxon: 'Stenonychosaurus inequalis',
  hip: 0.72,
  hp: 22,
  wanderSpeed: 2.8,
  fleeSpeed: 8.4,
  combatSpeed: 9.2,
  hearRange: 14,
  fleeRange: 0,
  spitRange: 0,
  peckRange: 1.1,
  lethalHp: 0,
  glb: '/models/dino/dino_troodon.glb',
  build: buildTroodonMesh,
};

const REX_HIDE = 0x6a5a3c;
const REX_BELLY = 0x9a8460;

/** Large vale patrol. Authored GLB at /models/dino/dino_rex.glb. */
export const REX_KIT: DinoKit = {
  kind: 'rex',
  taxon: 'Tyrannosaurus rex',
  hip: 3.1,
  hp: 420,
  wanderSpeed: 1.6,
  fleeSpeed: 4.2,
  combatSpeed: 5.4,
  hearRange: 28,
  fleeRange: 0,
  spitRange: 0,
  peckRange: 3.4,
  lethalHp: 0,
  glb: '/models/dino/dino_rex.glb',
  build: buildRexMesh,
};

const PARA_HIDE = 0x6a6a40;
const PARA_BELLY = 0xb0a070;
const PARA_CREST = 0x7a5a38;
const DEINO_HIDE = 0x6a3a28;
const DEINO_BELLY = 0x9a6a48;
const GALLI_HIDE = 0x8a7a50;
const GALLI_BELLY = 0xc4b888;
const TRIKE_HIDE = 0x6a5e48;
const TRIKE_FRILL = 0x4a4030;
const STEGO_HIDE = 0x5a5a38;
const STEGO_PLATE = 0x4a4a30;
const ANKY_HIDE = 0x4a4a42;
const ANKY_CLUB = 0x3a3830;
const CARNO_HIDE = 0x6a3a28;
const CARNO_HORN = 0x3a2a20;
const SPINO_HIDE = 0x3a4a42;
const SPINO_SAIL = 0x2a3a34;
const SPINO_BELLY = 0x7a6a48;
const PTERA_HIDE = 0x8a7a62;
const PTERA_WING = 0x5a4a38;

/** Herd hadrosaur. Crest alarm, stampede if the player is close and noisy. */
export const PARA_KIT: DinoKit = {
  kind: 'para',
  taxon: 'Parasaurolophus walkeri',
  hip: 2.2,
  hp: 110,
  wanderSpeed: 2.0,
  fleeSpeed: 7.2,
  combatSpeed: 7.8,
  hearRange: 22,
  fleeRange: 10,
  spitRange: 0,
  peckRange: 1.8,
  lethalHp: 0,
  glb: '/models/dino/dino_para.glb',
  build: buildParaMesh,
};

/** Heavy pack hunter. Authored GLB at /models/dino/dino_deinonychus.glb. */
export const DEINONYCHUS_KIT: DinoKit = {
  kind: 'deinonychus',
  taxon: 'Deinonychus antirrhopus',
  hip: 1.85,
  hp: 80,
  wanderSpeed: 2.1,
  fleeSpeed: 8.0,
  combatSpeed: 8.4,
  hearRange: 16,
  fleeRange: 0,
  spitRange: 0,
  peckRange: 1.6,
  lethalHp: 0,
  glb: '/models/dino/dino_deinonychus.glb',
  build: buildDeinonychusMesh,
};

/** Ostrich ornithomimid. Authored GLB at /models/dino/dino_galli.glb. Traffic and flee only. */
export const GALLI_KIT: DinoKit = {
  kind: 'galli',
  taxon: 'Gallimimus bullatus',
  hip: 1.75,
  hp: 55,
  wanderSpeed: 3.6,
  fleeSpeed: 10.8,
  combatSpeed: 10.8,
  hearRange: 16,
  fleeRange: 12,
  spitRange: 0,
  peckRange: 0,
  lethalHp: 0,
  glb: '/models/dino/dino_galli.glb',
  build: buildGalliMesh,
};

/** Charge ceratopsian. Do not tank the line. Authored GLB at /models/dino/dino_trike.glb. */
export const TRIKE_KIT: DinoKit = {
  kind: 'trike',
  taxon: 'Triceratops horridus',
  hip: 2.0,
  hp: 220,
  wanderSpeed: 1.1,
  fleeSpeed: 2.6,
  combatSpeed: 6.4,
  hearRange: 14,
  fleeRange: 0,
  spitRange: 0,
  peckRange: 2.6,
  lethalHp: 0,
  glb: '/models/dino/dino_trike.glb',
  build: buildTrikeMesh,
};

/** Plate-backed grazer. Tail zone is the threat. */
export const STEGO_KIT: DinoKit = {
  kind: 'stego',
  taxon: 'Stegosaurus stenops',
  hip: 1.7,
  hp: 200,
  wanderSpeed: 0.85,
  fleeSpeed: 1.7,
  combatSpeed: 1.4,
  hearRange: 12,
  fleeRange: 0,
  spitRange: 0,
  peckRange: 2.4,
  lethalHp: 0,
  glb: '/models/dino/dino_stego.glb',
  build: buildStegoMesh,
};

/** Club-tailed tank. Slow, close, heavy. Authored GLB at /models/dino/dino_anky.glb. */
export const ANKY_KIT: DinoKit = {
  kind: 'anky',
  taxon: 'Ankylosaurus magniventris',
  hip: 1.15,
  hp: 240,
  wanderSpeed: 0.7,
  fleeSpeed: 1.4,
  combatSpeed: 1.2,
  hearRange: 10,
  fleeRange: 0,
  spitRange: 0,
  peckRange: 2.2,
  lethalHp: 0,
  glb: '/models/dino/dino_anky.glb',
  build: buildAnkyMesh,
};

/** Sprint hunter. Outruns walk (3.2), not sprint + terrain (6.0). Authored GLB at /models/dino/dino_carno.glb. */
export const CARNO_KIT: DinoKit = {
  kind: 'carno',
  taxon: 'Carnotaurus sastrei',
  hip: 2.15,
  hp: 130,
  wanderSpeed: 2.5,
  fleeSpeed: 5.8,
  combatSpeed: 5.2,
  hearRange: 20,
  fleeRange: 0,
  spitRange: 0,
  peckRange: 1.8,
  lethalHp: 0,
  glb: '/models/dino/dino_carno.glb',
  build: buildCarnoMesh,
};

/** River apex. Authored GLB at /models/dino/dino_spino.glb. */
export const SPINO_KIT: DinoKit = {
  kind: 'spino',
  taxon: 'Spinosaurus aegyptiacus',
  hip: 2.2,
  hp: 400,
  wanderSpeed: 1.5,
  fleeSpeed: 3.6,
  combatSpeed: 4.4,
  hearRange: 24,
  fleeRange: 0,
  spitRange: 0,
  peckRange: 4.0,
  lethalHp: 0,
  glb: '/models/dino/dino_spino.glb',
  build: buildSpinoMesh,
};

/** Aerial scout. Alarms from y>6; dive peck is the only contact. Authored GLB at /models/dino/dino_ptera.glb. */
export const PTERA_KIT: DinoKit = {
  kind: 'ptera',
  taxon: 'Pteranodon longiceps',
  hip: 0.55,
  hp: 35,
  wanderSpeed: 5.0,
  fleeSpeed: 8.0,
  combatSpeed: 6.5,
  hearRange: 28,
  fleeRange: 8,
  spitRange: 0,
  peckRange: 1.2,
  lethalHp: 0,
  glb: '/models/dino/dino_ptera.glb',
  build: buildPteraMesh,
};

/** All fifteen real-taxon kits. Order is stable for tests. */
export const ALL_KITS: readonly DinoKit[] = [
  COMPY_KIT,
  DILOPH_KIT,
  PARA_KIT,
  RAPTOR_KIT,
  DEINONYCHUS_KIT,
  GALLI_KIT,
  TRIKE_KIT,
  STEGO_KIT,
  ANKY_KIT,
  CARNO_KIT,
  BARYONYX_KIT,
  SPINO_KIT,
  REX_KIT,
  PTERA_KIT,
  TROODON_KIT,
];

/** Chicken-theropod, hip 0.4 m. Origin at feet, facing +Z. */
export function buildCompyMesh(): THREE.Group {
  const root = new THREE.Group();
  const hide = mat(COMPY_HIDE);
  const belly = mat(COMPY_BELLY);
  const claw = mat(CLAW);

  const body = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.11, 0.24), hide);
  body.position.set(0, 0.4, 0.02);
  body.name = 'body';

  const keel = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.05, 0.18), belly);
  keel.position.set(0, 0.34, 0.03);

  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.022, 0.03, 0.1, 6), hide);
  neck.position.set(0, 0.48, 0.13);
  neck.rotation.x = 0.7;

  const head = new THREE.Mesh(new THREE.BoxGeometry(0.055, 0.045, 0.09), hide);
  head.position.set(0, 0.545, 0.2);

  const snout = new THREE.Mesh(new THREE.BoxGeometry(0.032, 0.022, 0.055), belly);
  snout.position.set(0, 0.535, 0.26);

  const tail0 = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.055, 0.16), hide);
  tail0.position.set(0, 0.385, -0.16);
  const tail1 = new THREE.Mesh(new THREE.BoxGeometry(0.042, 0.038, 0.16), hide);
  tail1.position.set(0, 0.36, -0.3);
  const tail2 = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.018, 0.14), hide);
  tail2.position.set(0, 0.345, -0.44);

  root.add(body, keel, neck, head, snout, tail0, tail1, tail2);
  root.add(makeLeg(-0.045, 0.16, 0.03, 0.22, hide, claw, 'legL'));
  root.add(makeLeg(0.045, 0.16, 0.03, 0.22, hide, claw, 'legR'));
  return root;
}

/**
 * Slender crested theropod, hip 1.4 m. Twin cranial crests (real taxon).
 * Two neck planes are a display ruff tell — not a franchise neck-fan.
 */
export function buildDilophMesh(): THREE.Group {
  const root = new THREE.Group();
  const hide = mat(DILOPH_HIDE);
  const belly = mat(DILOPH_BELLY);
  const crestMat = mat(DILOPH_CREST, true);
  const frillMat = mat(DILOPH_FRILL, true);
  const claw = mat(CLAW);

  const body = new THREE.Mesh(new THREE.BoxGeometry(0.38, 0.36, 0.95), hide);
  body.position.set(0, 1.42, 0.04);
  body.name = 'body';

  const keel = new THREE.Mesh(new THREE.BoxGeometry(0.26, 0.16, 0.72), belly);
  keel.position.set(0, 1.2, 0.06);

  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.11, 0.42, 8), hide);
  neck.position.set(0, 1.68, 0.5);
  neck.rotation.x = 0.55;

  const head = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.13, 0.3), hide);
  head.position.set(0, 1.9, 0.76);

  const jaw = new THREE.Mesh(new THREE.BoxGeometry(0.11, 0.07, 0.28), belly);
  jaw.position.set(0, 1.82, 0.98);

  const crestL = new THREE.Mesh(new THREE.PlaneGeometry(0.2, 0.16), crestMat);
  crestL.position.set(-0.055, 2.02, 0.74);
  crestL.rotation.y = Math.PI / 2;
  crestL.rotation.z = 0.18;
  crestL.name = 'crestL';

  const crestR = new THREE.Mesh(new THREE.PlaneGeometry(0.2, 0.16), crestMat);
  crestR.position.set(0.055, 2.02, 0.74);
  crestR.rotation.y = -Math.PI / 2;
  crestR.rotation.z = -0.18;
  crestR.name = 'crestR';

  const frillL = new THREE.Mesh(new THREE.PlaneGeometry(0.42, 0.5), frillMat);
  frillL.position.set(-0.08, 1.66, 0.42);
  frillL.rotation.y = 0.22;
  frillL.name = 'frillL';

  const frillR = new THREE.Mesh(new THREE.PlaneGeometry(0.42, 0.5), frillMat);
  frillR.position.set(0.08, 1.66, 0.42);
  frillR.rotation.y = -0.22;
  frillR.name = 'frillR';

  const tail0 = new THREE.Mesh(new THREE.BoxGeometry(0.26, 0.22, 0.7), hide);
  tail0.position.set(0, 1.36, -0.62);
  const tail1 = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.14, 0.7), hide);
  tail1.position.set(0, 1.28, -1.26);
  const tail2 = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.07, 0.62), hide);
  tail2.position.set(0, 1.22, -1.86);

  root.add(body, keel, neck, head, jaw, crestL, crestR, frillL, frillR, tail0, tail1, tail2);
  root.add(makeLeg(-0.14, 0.62, 0.08, 0.95, hide, claw, 'legL'));
  root.add(makeLeg(0.14, 0.62, 0.08, 0.95, hide, claw, 'legR'));
  return root;
}

/** Low-slung piscivore ~8 m. Origin at the waterline, facing +Z. */
export function buildBaryonyxMesh(): THREE.Group {
  const root = new THREE.Group();
  const hide = mat(BARY_HIDE);
  const belly = mat(BARY_BELLY);
  const claw = mat(BARY_CLAW);

  const body = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.52, 3.1), hide);
  body.position.set(0, 0.55, 0.1);
  body.name = 'body';

  const keel = new THREE.Mesh(new THREE.BoxGeometry(0.48, 0.22, 2.4), belly);
  keel.position.set(0, 0.28, 0.15);

  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.18, 0.85, 8), hide);
  neck.position.set(0, 0.62, 1.85);
  neck.rotation.x = 0.85;

  const head = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.16, 0.7), hide);
  head.position.set(0, 0.48, 2.55);

  const jaw = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.08, 0.62), belly);
  jaw.position.set(0, 0.38, 2.62);

  const crest = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.12, 0.22), hide);
  crest.position.set(0, 0.6, 2.38);

  const tail0 = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.38, 1.6), hide);
  tail0.position.set(0, 0.5, -2.1);
  const tail1 = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.22, 1.5), hide);
  tail1.position.set(0, 0.42, -3.5);
  const tail2 = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.1, 1.2), hide);
  tail2.position.set(0, 0.34, -4.7);

  root.add(body, keel, neck, head, jaw, crest, tail0, tail1, tail2);
  root.add(makeLeg(-0.28, 0.28, 0.09, 0.42, hide, claw, 'legL'));
  root.add(makeLeg(0.28, 0.28, 0.09, 0.42, hide, claw, 'legR'));
  return root;
}

/** Slender pack hunter, hip 1.6 m. Sickle claw on the right foot. */
export function buildRaptorMesh(): THREE.Group {
  const root = new THREE.Group();
  const hide = mat(RAPTOR_HIDE);
  const belly = mat(RAPTOR_BELLY);
  const stripe = mat(RAPTOR_STRIPE);
  const claw = mat(CLAW);

  const body = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.3, 0.85), hide);
  body.position.set(0, 1.62, 0.04);
  body.name = 'body';

  const keel = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.14, 0.62), belly);
  keel.position.set(0, 1.42, 0.06);

  const stripeMesh = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.08, 0.7), stripe);
  stripeMesh.position.set(0, 1.74, 0.02);

  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.09, 0.32, 7), hide);
  neck.position.set(0, 1.82, 0.44);
  neck.rotation.x = 0.62;

  const head = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.1, 0.22), hide);
  head.position.set(0, 1.98, 0.64);

  const snout = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.05, 0.16), belly);
  snout.position.set(0, 1.94, 0.82);

  const tail0 = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.16, 0.7), hide);
  tail0.position.set(0, 1.56, -0.52);
  const tail1 = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.09, 0.7), hide);
  tail1.position.set(0, 1.5, -1.16);
  const tail2 = new THREE.Mesh(new THREE.BoxGeometry(0.045, 0.04, 0.55), hide);
  tail2.position.set(0, 1.46, -1.72);

  const sickle = new THREE.Mesh(new THREE.ConeGeometry(0.028, 0.14, 5), claw);
  sickle.position.set(0.12, 0.08, 0.14);
  sickle.rotation.x = 1.15;
  sickle.name = 'sickle';

  root.add(body, keel, stripeMesh, neck, head, snout, tail0, tail1, tail2, sickle);
  root.add(makeLeg(-0.11, 0.72, 0.055, 1.05, hide, claw, 'legL'));
  root.add(makeLeg(0.11, 0.72, 0.055, 1.05, hide, claw, 'legR'));
  return root;
}

/** Gracile shaft hunter, hip 0.72 m. Large orbits, origin at feet, facing +Z. */
export function buildTroodonMesh(): THREE.Group {
  const root = new THREE.Group();
  const hide = mat(TROODON_HIDE);
  const belly = mat(TROODON_BELLY);
  const claw = mat(CLAW);
  const eyeMat = mat(TROODON_EYE);

  const body = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.13, 0.38), hide);
  body.position.set(0, 0.72, 0.02);
  body.name = 'body';

  const keel = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.06, 0.28), belly);
  keel.position.set(0, 0.63, 0.03);

  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.028, 0.038, 0.16, 7), hide);
  neck.position.set(0, 0.82, 0.22);
  neck.rotation.x = 0.55;

  const head = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.07, 0.12), hide);
  head.position.set(0, 0.92, 0.32);

  const snout = new THREE.Mesh(new THREE.BoxGeometry(0.035, 0.025, 0.10), belly);
  snout.position.set(0, 0.89, 0.42);

  const eyeL = new THREE.Mesh(new THREE.SphereGeometry(0.022, 6, 5), eyeMat);
  eyeL.position.set(-0.038, 0.94, 0.34);
  eyeL.name = 'eyeL';
  const eyeR = new THREE.Mesh(new THREE.SphereGeometry(0.022, 6, 5), eyeMat);
  eyeR.position.set(0.038, 0.94, 0.34);
  eyeR.name = 'eyeR';

  const tail0 = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.07, 0.32), hide);
  tail0.position.set(0, 0.70, -0.24);
  const tail1 = new THREE.Mesh(new THREE.BoxGeometry(0.045, 0.04, 0.32), hide);
  tail1.position.set(0, 0.67, -0.54);
  const tail2 = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.018, 0.26), hide);
  tail2.position.set(0, 0.65, -0.80);

  const sickle = new THREE.Mesh(new THREE.ConeGeometry(0.014, 0.07, 5), claw);
  sickle.position.set(0.055, 0.06, 0.06);
  sickle.rotation.x = 1.15;
  sickle.name = 'sickle';

  root.add(body, keel, neck, head, snout, eyeL, eyeR, tail0, tail1, tail2, sickle);
  root.add(makeLeg(-0.05, 0.32, 0.028, 0.48, hide, claw, 'legL'));
  root.add(makeLeg(0.05, 0.32, 0.028, 0.48, hide, claw, 'legR'));
  return stamp(root);
}

/** Heavy theropod, hip 3.1 m. Origin at feet, facing +Z. */
export function buildRexMesh(): THREE.Group {
  const root = new THREE.Group();
  const hide = mat(REX_HIDE);
  const belly = mat(REX_BELLY);
  const claw = mat(CLAW);

  const body = new THREE.Mesh(new THREE.BoxGeometry(1.15, 1.05, 2.6), hide);
  body.position.set(0, 3.15, 0.1);
  body.name = 'body';

  const keel = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.42, 2.0), belly);
  keel.position.set(0, 2.55, 0.15);

  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.34, 0.9, 8), hide);
  neck.position.set(0, 3.55, 1.45);
  neck.rotation.x = 0.55;

  const head = new THREE.Mesh(new THREE.BoxGeometry(0.48, 0.4, 0.95), hide);
  head.position.set(0, 3.85, 2.15);

  const jaw = new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.16, 0.85), belly);
  jaw.position.set(0, 3.58, 2.22);

  const tail0 = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.58, 1.8), hide);
  tail0.position.set(0, 2.95, -1.7);
  const tail1 = new THREE.Mesh(new THREE.BoxGeometry(0.38, 0.32, 1.7), hide);
  tail1.position.set(0, 2.75, -3.3);
  const tail2 = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.14, 1.3), hide);
  tail2.position.set(0, 2.6, -4.6);

  root.add(body, keel, neck, head, jaw, tail0, tail1, tail2);
  root.add(makeLeg(-0.42, 1.45, 0.16, 2.2, hide, claw, 'legL'));
  root.add(makeLeg(0.42, 1.45, 0.16, 2.2, hide, claw, 'legR'));
  return root;
}

/** Quadruped hadrosaur, hip 2.2 m. Tubular crest, origin at feet, facing +Z. */
export function buildParaMesh(): THREE.Group {
  const root = new THREE.Group();
  const hide = mat(PARA_HIDE);
  const belly = mat(PARA_BELLY);
  const crestMat = mat(PARA_CREST);
  const claw = mat(CLAW);

  const body = new THREE.Mesh(new THREE.BoxGeometry(0.85, 0.85, 2.2), hide);
  body.position.set(0, 2.2, 0.05);
  body.name = 'body';

  const keel = new THREE.Mesh(new THREE.BoxGeometry(0.58, 0.32, 1.7), belly);
  keel.position.set(0, 1.7, 0.08);

  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.22, 0.7, 8), hide);
  neck.position.set(0, 2.45, 1.2);
  neck.rotation.x = 0.7;

  const head = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.2, 0.48), hide);
  head.position.set(0, 2.55, 1.7);

  const bill = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.1, 0.32), belly);
  bill.position.set(0, 2.46, 2.05);

  const crest = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.07, 0.95, 8), crestMat);
  crest.position.set(0, 3.05, 1.42);
  crest.rotation.x = -0.95;
  crest.name = 'crest';

  const tail0 = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.42, 1.4), hide);
  tail0.position.set(0, 2.05, -1.5);
  const tail1 = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.24, 1.3), hide);
  tail1.position.set(0, 1.95, -2.7);
  const tail2 = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.1, 1.0), hide);
  tail2.position.set(0, 1.88, -3.7);

  root.add(body, keel, neck, head, bill, crest, tail0, tail1, tail2);
  root.add(makeQuadLeg(-0.32, 0.55, 0.85, 0.1, 1.65, hide, claw, 'legFL'));
  root.add(makeQuadLeg(0.32, 0.55, 0.85, 0.1, 1.65, hide, claw, 'legFR'));
  root.add(makeQuadLeg(-0.34, -0.55, 0.85, 0.11, 1.65, hide, claw, 'legL'));
  root.add(makeQuadLeg(0.34, -0.55, 0.85, 0.11, 1.65, hide, claw, 'legR'));
  return stamp(root);
}

/** Heavier dromaeosaur, hip 1.85 m. Twin sickle claws, thicker torso. */
export function buildDeinonychusMesh(): THREE.Group {
  const root = new THREE.Group();
  const hide = mat(DEINO_HIDE);
  const belly = mat(DEINO_BELLY);
  const claw = mat(CLAW);

  const body = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.4, 1.05), hide);
  body.position.set(0, 1.88, 0.04);
  body.name = 'body';

  const keel = new THREE.Mesh(new THREE.BoxGeometry(0.26, 0.16, 0.78), belly);
  keel.position.set(0, 1.64, 0.06);

  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.12, 0.38, 7), hide);
  neck.position.set(0, 2.12, 0.55);
  neck.rotation.x = 0.55;

  const head = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.13, 0.3), hide);
  head.position.set(0, 2.28, 0.82);

  const snout = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.06, 0.2), belly);
  snout.position.set(0, 2.22, 1.04);

  const tail0 = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.2, 0.85), hide);
  tail0.position.set(0, 1.8, -0.65);
  const tail1 = new THREE.Mesh(new THREE.BoxGeometry(0.13, 0.11, 0.8), hide);
  tail1.position.set(0, 1.74, -1.4);
  const tail2 = new THREE.Mesh(new THREE.BoxGeometry(0.055, 0.05, 0.6), hide);
  tail2.position.set(0, 1.7, -2.05);

  const sickleL = new THREE.Mesh(new THREE.ConeGeometry(0.034, 0.18, 5), claw);
  sickleL.position.set(-0.14, 0.09, 0.16);
  sickleL.rotation.x = 1.15;
  sickleL.name = 'sickleL';
  const sickleR = new THREE.Mesh(new THREE.ConeGeometry(0.034, 0.18, 5), claw);
  sickleR.position.set(0.14, 0.09, 0.16);
  sickleR.rotation.x = 1.15;
  sickleR.name = 'sickle';

  root.add(body, keel, neck, head, snout, tail0, tail1, tail2, sickleL, sickleR);
  root.add(makeLeg(-0.14, 0.82, 0.065, 1.2, hide, claw, 'legL'));
  root.add(makeLeg(0.14, 0.82, 0.065, 1.2, hide, claw, 'legR'));
  return stamp(root);
}

/** Slender ornithomimid, hip 1.75 m. Long neck, tiny skull, ostrich legs. */
export function buildGalliMesh(): THREE.Group {
  const root = new THREE.Group();
  const hide = mat(GALLI_HIDE);
  const belly = mat(GALLI_BELLY);
  const claw = mat(CLAW);

  const body = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.28, 0.85), hide);
  body.position.set(0, 1.78, 0.02);
  body.name = 'body';

  const keel = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.12, 0.62), belly);
  keel.position.set(0, 1.6, 0.04);

  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.08, 0.85, 7), hide);
  neck.position.set(0, 2.25, 0.55);
  neck.rotation.x = 0.45;

  const head = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.07, 0.16), hide);
  head.position.set(0, 2.68, 0.92);

  const beak = new THREE.Mesh(new THREE.BoxGeometry(0.045, 0.03, 0.1), belly);
  beak.position.set(0, 2.64, 1.04);

  const armL = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.04, 0.22), hide);
  armL.position.set(-0.16, 1.7, 0.28);
  const armR = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.04, 0.22), hide);
  armR.position.set(0.16, 1.7, 0.28);

  const tail0 = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.14, 0.7), hide);
  tail0.position.set(0, 1.72, -0.55);
  const tail1 = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.07, 0.7), hide);
  tail1.position.set(0, 1.68, -1.2);
  const tail2 = new THREE.Mesh(new THREE.BoxGeometry(0.035, 0.03, 0.5), hide);
  tail2.position.set(0, 1.66, -1.75);

  root.add(body, keel, neck, head, beak, armL, armR, tail0, tail1, tail2);
  root.add(makeLeg(-0.1, 0.82, 0.045, 1.25, hide, claw, 'legL'));
  root.add(makeLeg(0.1, 0.82, 0.045, 1.25, hide, claw, 'legR'));
  return stamp(root);
}

/** Broad ceratopsian, hip 2.0 m. Three horns and a bony frill. */
export function buildTrikeMesh(): THREE.Group {
  const root = new THREE.Group();
  const hide = mat(TRIKE_HIDE);
  const frillMat = mat(TRIKE_FRILL);
  const claw = mat(CLAW);

  const body = new THREE.Mesh(new THREE.BoxGeometry(1.35, 1.15, 2.4), hide);
  body.position.set(0, 2.05, -0.1);
  body.name = 'body';

  const keel = new THREE.Mesh(new THREE.BoxGeometry(0.95, 0.4, 1.8), mat(0x8a7a58));
  keel.position.set(0, 1.4, -0.05);

  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.38, 0.55, 8), hide);
  neck.position.set(0, 2.15, 1.2);
  neck.rotation.x = 0.35;

  const head = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.42, 0.7), hide);
  head.position.set(0, 2.15, 1.7);

  const frill = new THREE.Mesh(new THREE.BoxGeometry(1.55, 1.15, 0.12), frillMat);
  frill.position.set(0, 2.7, 1.25);
  frill.rotation.x = -0.28;
  frill.name = 'frill';

  const hornN = new THREE.Mesh(new THREE.ConeGeometry(0.05, 0.28, 6), claw);
  hornN.position.set(0, 2.28, 2.08);
  hornN.rotation.x = 1.2;
  const hornL = new THREE.Mesh(new THREE.ConeGeometry(0.055, 0.7, 6), claw);
  hornL.position.set(-0.18, 2.55, 1.95);
  hornL.rotation.x = 1.05;
  const hornR = new THREE.Mesh(new THREE.ConeGeometry(0.055, 0.7, 6), claw);
  hornR.position.set(0.18, 2.55, 1.95);
  hornR.rotation.x = 1.05;

  const tail0 = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.45, 0.9), hide);
  tail0.position.set(0, 1.85, -1.55);

  root.add(body, keel, neck, head, frill, hornN, hornL, hornR, tail0);
  root.add(makeQuadLeg(-0.48, 0.7, 0.85, 0.14, 1.65, hide, claw, 'legFL'));
  root.add(makeQuadLeg(0.48, 0.7, 0.85, 0.14, 1.65, hide, claw, 'legFR'));
  root.add(makeQuadLeg(-0.52, -0.7, 0.85, 0.16, 1.65, hide, claw, 'legL'));
  root.add(makeQuadLeg(0.52, -0.7, 0.85, 0.16, 1.65, hide, claw, 'legR'));
  return stamp(root);
}

/** Plate-backed stegosaur, hip 1.7 m. Double plate row and thagomizer. */
export function buildStegoMesh(): THREE.Group {
  const root = new THREE.Group();
  const hide = mat(STEGO_HIDE);
  const plateMat = mat(STEGO_PLATE, true);
  const claw = mat(CLAW);

  const body = new THREE.Mesh(new THREE.BoxGeometry(0.85, 0.75, 2.6), hide);
  body.position.set(0, 1.7, 0);
  body.name = 'body';

  const keel = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.28, 2.0), mat(0x8a8460));
  keel.position.set(0, 1.25, 0.05);

  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.18, 0.55, 7), hide);
  neck.position.set(0, 1.55, 1.45);
  neck.rotation.x = 0.85;

  const head = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.12, 0.32), hide);
  head.position.set(0, 1.35, 1.85);

  const tail0 = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.32, 1.3), hide);
  tail0.position.set(0, 1.55, -1.7);
  const tail1 = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.18, 1.1), hide);
  tail1.position.set(0, 1.4, -2.75);
  tail1.name = 'tailClub';

  const spikeA = new THREE.Mesh(new THREE.ConeGeometry(0.04, 0.45, 5), claw);
  spikeA.position.set(-0.1, 1.62, -3.2);
  spikeA.rotation.x = 0.9;
  const spikeB = new THREE.Mesh(new THREE.ConeGeometry(0.04, 0.45, 5), claw);
  spikeB.position.set(0.1, 1.62, -3.2);
  spikeB.rotation.x = 0.9;
  const spikeC = new THREE.Mesh(new THREE.ConeGeometry(0.035, 0.38, 5), claw);
  spikeC.position.set(-0.08, 1.48, -3.4);
  spikeC.rotation.x = 1.15;
  const spikeD = new THREE.Mesh(new THREE.ConeGeometry(0.035, 0.38, 5), claw);
  spikeD.position.set(0.08, 1.48, -3.4);
  spikeD.rotation.x = 1.15;

  root.add(body, keel, neck, head, tail0, tail1, spikeA, spikeB, spikeC, spikeD);
  const plateZs = [-0.9, -0.35, 0.2, 0.75, 1.15];
  for (let i = 0; i < plateZs.length; i++) {
    const z = plateZs[i] ?? 0;
    const h = 0.55 + (i % 3) * 0.18;
    const plate = new THREE.Mesh(new THREE.PlaneGeometry(0.08, h), plateMat);
    plate.position.set(i % 2 === 0 ? -0.08 : 0.08, 2.15 + h * 0.15, z);
    plate.name = `plate${i}`;
    root.add(plate);
  }
  root.add(makeQuadLeg(-0.3, 0.7, 0.7, 0.09, 1.35, hide, claw, 'legFL'));
  root.add(makeQuadLeg(0.3, 0.7, 0.7, 0.09, 1.35, hide, claw, 'legFR'));
  root.add(makeQuadLeg(-0.32, -0.75, 0.7, 0.1, 1.35, hide, claw, 'legL'));
  root.add(makeQuadLeg(0.32, -0.75, 0.7, 0.1, 1.35, hide, claw, 'legR'));
  return stamp(root);
}

/** Low armored ankylosaur, hip 1.15 m. Club tail, osteoderm nubs. */
export function buildAnkyMesh(): THREE.Group {
  const root = new THREE.Group();
  const hide = mat(ANKY_HIDE);
  const clubMat = mat(ANKY_CLUB);
  const claw = mat(CLAW);

  const body = new THREE.Mesh(new THREE.BoxGeometry(1.35, 0.7, 2.2), hide);
  body.position.set(0, 1.15, 0);
  body.name = 'body';

  const keel = new THREE.Mesh(new THREE.BoxGeometry(0.95, 0.22, 1.7), mat(0x6a6858));
  keel.position.set(0, 0.72, 0.05);

  const head = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.32, 0.5), hide);
  head.position.set(0, 1.1, 1.3);

  const beak = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.16, 0.22), clubMat);
  beak.position.set(0, 1.0, 1.6);

  const nubs: [number, number, number][] = [
    [-0.5, 1.52, 0.4],
    [0.5, 1.52, 0.4],
    [-0.45, 1.52, -0.4],
    [0.45, 1.52, -0.4],
    [0, 1.55, 0],
  ];
  for (let i = 0; i < nubs.length; i++) {
    const n = nubs[i];
    if (!n) {
      continue;
    }
    const nub = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.12, 0.18), clubMat);
    nub.position.set(n[0], n[1], n[2]);
    root.add(nub);
  }

  const tail0 = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.28, 1.1), hide);
  tail0.position.set(0, 1.0, -1.5);
  const club = new THREE.Mesh(new THREE.SphereGeometry(0.28, 7, 6), clubMat);
  club.position.set(0, 0.95, -2.2);
  club.scale.set(1.15, 0.75, 1.35);
  club.name = 'tailClub';

  root.add(body, keel, head, beak, tail0, club);
  root.add(makeQuadLeg(-0.45, 0.65, 0.5, 0.11, 0.95, hide, claw, 'legFL'));
  root.add(makeQuadLeg(0.45, 0.65, 0.5, 0.11, 0.95, hide, claw, 'legFR'));
  root.add(makeQuadLeg(-0.48, -0.6, 0.5, 0.12, 0.95, hide, claw, 'legL'));
  root.add(makeQuadLeg(0.48, -0.6, 0.5, 0.12, 0.95, hide, claw, 'legR'));
  return stamp(root);
}

/** Horned abelisaur, hip 2.15 m. Tiny arms, brow horns, thick tail. */
export function buildCarnoMesh(): THREE.Group {
  const root = new THREE.Group();
  const hide = mat(CARNO_HIDE);
  const horn = mat(CARNO_HORN);
  const claw = mat(CLAW);

  const body = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.7, 1.7), hide);
  body.position.set(0, 2.18, 0.04);
  body.name = 'body';

  const keel = new THREE.Mesh(new THREE.BoxGeometry(0.48, 0.26, 1.25), mat(0x8a6a48));
  keel.position.set(0, 1.75, 0.08);

  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.24, 0.55, 8), hide);
  neck.position.set(0, 2.5, 0.95);
  neck.rotation.x = 0.4;

  const head = new THREE.Mesh(new THREE.BoxGeometry(0.32, 0.28, 0.55), hide);
  head.position.set(0, 2.72, 1.38);

  const jaw = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.1, 0.48), mat(0x8a6a48));
  jaw.position.set(0, 2.52, 1.42);

  const hornL = new THREE.Mesh(new THREE.ConeGeometry(0.06, 0.18, 5), horn);
  hornL.position.set(-0.14, 2.92, 1.28);
  const hornR = new THREE.Mesh(new THREE.ConeGeometry(0.06, 0.18, 5), horn);
  hornR.position.set(0.14, 2.92, 1.28);

  const stubL = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.06, 0.12), hide);
  stubL.position.set(-0.32, 1.95, 0.45);
  const stubR = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.06, 0.12), hide);
  stubR.position.set(0.32, 1.95, 0.45);

  const tail0 = new THREE.Mesh(new THREE.BoxGeometry(0.48, 0.4, 1.2), hide);
  tail0.position.set(0, 2.05, -1.2);
  const tail1 = new THREE.Mesh(new THREE.BoxGeometry(0.26, 0.22, 1.1), hide);
  tail1.position.set(0, 1.95, -2.25);
  const tail2 = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.1, 0.8), hide);
  tail2.position.set(0, 1.88, -3.1);

  root.add(body, keel, neck, head, jaw, hornL, hornR, stubL, stubR, tail0, tail1, tail2);
  root.add(makeLeg(-0.24, 0.95, 0.1, 1.5, hide, claw, 'legL'));
  root.add(makeLeg(0.24, 0.95, 0.1, 1.5, hide, claw, 'legR'));
  return stamp(root);
}

/** Sail-backed river apex, hip 2.2 m. Long snout, tall neural sail. */
export function buildSpinoMesh(): THREE.Group {
  const root = new THREE.Group();
  const hide = mat(SPINO_HIDE);
  const sailMat = mat(SPINO_SAIL, true);
  const belly = mat(SPINO_BELLY);
  const claw = mat(CLAW);

  const body = new THREE.Mesh(new THREE.BoxGeometry(0.85, 0.75, 3.2), hide);
  body.position.set(0, 2.2, 0.1);
  body.name = 'body';

  const keel = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.28, 2.5), belly);
  keel.position.set(0, 1.75, 0.15);

  const sail = new THREE.Mesh(new THREE.PlaneGeometry(2.4, 1.5), sailMat);
  sail.position.set(0, 3.15, -0.05);
  sail.rotation.y = Math.PI / 2;
  sail.name = 'sail';

  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.22, 1.1, 8), hide);
  neck.position.set(0, 2.15, 1.95);
  neck.rotation.x = 0.75;

  const head = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.18, 1.15), hide);
  head.position.set(0, 1.85, 2.85);

  const jaw = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.08, 1.05), belly);
  jaw.position.set(0, 1.7, 2.9);

  const tail0 = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.45, 1.8), hide);
  tail0.position.set(0, 2.05, -2.15);
  const tail1 = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.24, 1.6), hide);
  tail1.position.set(0, 1.9, -3.7);
  const tail2 = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.1, 1.2), hide);
  tail2.position.set(0, 1.78, -5.0);

  root.add(body, keel, sail, neck, head, jaw, tail0, tail1, tail2);
  root.add(makeLeg(-0.32, 0.95, 0.12, 1.55, hide, claw, 'legL'));
  root.add(makeLeg(0.32, 0.95, 0.12, 1.55, hide, claw, 'legR'));
  return stamp(root);
}

/** Crested pterosaur. Origin at feet, wings spread, facing +Z. */
export function buildPteraMesh(): THREE.Group {
  const root = new THREE.Group();
  const hide = mat(PTERA_HIDE);
  const wingMat = mat(PTERA_WING, true);
  const claw = mat(CLAW);

  const body = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.14, 0.55), hide);
  body.position.set(0, 0.58, 0.02);
  body.name = 'body';

  const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.045, 0.28, 6), hide);
  neck.position.set(0, 0.68, 0.32);
  neck.rotation.x = 0.55;

  const head = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.06, 0.28), hide);
  head.position.set(0, 0.78, 0.52);

  const beak = new THREE.Mesh(new THREE.BoxGeometry(0.035, 0.025, 0.22), claw);
  beak.position.set(0, 0.76, 0.72);

  const crest = new THREE.Mesh(new THREE.PlaneGeometry(0.08, 0.28), hide);
  crest.position.set(0, 0.96, 0.42);
  crest.rotation.y = Math.PI / 2;
  crest.name = 'crest';

  const wingL = new THREE.Mesh(new THREE.PlaneGeometry(1.7, 0.45), wingMat);
  wingL.position.set(-0.9, 0.62, 0);
  wingL.rotation.x = 0.12;
  wingL.name = 'wingL';
  const wingR = new THREE.Mesh(new THREE.PlaneGeometry(1.7, 0.45), wingMat);
  wingR.position.set(0.9, 0.62, 0);
  wingR.rotation.x = 0.12;
  wingR.name = 'wingR';

  const tail = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.04, 0.18), hide);
  tail.position.set(0, 0.55, -0.32);

  root.add(body, neck, head, beak, crest, wingL, wingR, tail);
  root.add(makeLeg(-0.05, 0.22, 0.018, 0.35, hide, claw, 'legL'));
  root.add(makeLeg(0.05, 0.22, 0.018, 0.35, hide, claw, 'legR'));
  return stamp(root);
}

function makeLeg(
  x: number,
  y: number,
  radius: number,
  height: number,
  hide: THREE.Material,
  claw: THREE.Material,
  name: string,
): THREE.Group {
  const leg = new THREE.Group();
  leg.name = name;
  const shin = new THREE.Mesh(new THREE.CylinderGeometry(radius * 0.7, radius, height, 6), hide);
  shin.position.set(x, y, 0.02);
  const foot = new THREE.Mesh(new THREE.BoxGeometry(radius * 2.2, radius * 0.55, radius * 3.4), claw);
  foot.position.set(x, 0.03, 0.06);
  leg.add(shin, foot);
  return leg;
}

function makeQuadLeg(
  x: number,
  z: number,
  y: number,
  radius: number,
  height: number,
  hide: THREE.Material,
  claw: THREE.Material,
  name: string,
): THREE.Group {
  const leg = new THREE.Group();
  leg.name = name;
  const shin = new THREE.Mesh(new THREE.CylinderGeometry(radius * 0.7, radius, height, 6), hide);
  shin.position.set(x, y, z);
  const foot = new THREE.Mesh(new THREE.BoxGeometry(radius * 2.2, radius * 0.55, radius * 3.4), claw);
  foot.position.set(x, 0.03, z + 0.08);
  leg.add(shin, foot);
  return leg;
}

function stamp(root: THREE.Group): THREE.Group {
  root.traverse((obj) => {
    if (obj instanceof THREE.Mesh) {
      obj.castShadow = true;
      obj.receiveShadow = false;
    }
  });
  return root;
}

function mat(color: number, doubleSide = false): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    roughness: 0.86,
    metalness: 0.04,
    side: doubleSide ? THREE.DoubleSide : THREE.FrontSide,
  });
}
